import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import { ConditionalWrapper } from 'components/conditional-wrapper';
import FullPageScreen from 'components/full-page-screen';
import LoadingAnimation from 'components/loading-animation';
import PaymentMethod from 'components/payment-method';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import { useOrderContext } from 'state/order';
import { useCartEntriesDayParts } from 'state/order/hooks/use-cart-entries-day-parts';
import { useOrderTimedFire } from 'state/order/hooks/use-order-timed-fire';
import { usePaymentContext } from 'state/payment';
import { CASH_ACCOUNT_IDENTIFIER } from 'state/payment/constants';
import { useServiceModeContext } from 'state/service-mode';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import logger from 'utils/logger';
import { routes } from 'utils/routing';

import { PlaceOrderButton } from '../../../place-order-button';
import SectionHeading from '../../../section-heading';
import { AddNewCreditCardWrapper } from '../../add-new-credit-card-wrapper';
import DeliveryCancellationPolicy from '../../delivery-cancellation-policy';
import PaymentSelection from '../../payment-selection';
import { PaymentInfoBackground, PaymentSection } from '../../styled';
import { OrderPaymentProps } from '../../types';
import Totals from '../Totals';
import { useOrderPayment } from '../use-order-payment';

import { FirstpayHostedPaymentForm } from './firstpay-form/firstpay-hosted-payment-form';
import { IFirstpayPaymentVaultLinkResult, OutcomeResult } from './types';

const StyledFullPageScreen = styled(FullPageScreen)`
  background: ${Styles.color.background};

  h1 {
    text-transform: capitalize;
  }
`;

export const FirstpayHostedPagePayment: React.FC<OrderPaymentProps> = ({
  serverOrder,
  cartEntries,
  onPriceOrder,
  pricedAndAvailable,
}) => {
  const { formatMessage } = useIntl();
  const [vaultedPaymentData, setVaultedPaymentData] =
    useState<IFirstpayPaymentVaultLinkResult | null>(null);

  const [isCheckoutErrorsModalOpenFirstpay, setIsCheckoutErrorsModalOpenFirstpay] = useState(false);
  const [isPaymentError, setIsPaymentError] = useState(false);
  const [isProcessingPaymentFirstpay, setIsProcessingPaymentFirstpay] = useState(false);
  const { cartEntriesActiveDayParts } = useCartEntriesDayParts(cartEntries);
  const order = useOrderContext();
  const { serviceMode, isDelivery, isCurbside, isTableService } = useServiceModeContext();
  const { checkoutPaymentMethodId } = usePaymentContext();
  const { enableOrderTimedFire } = useOrderTimedFire({
    serverOrder,
    cartEntriesActiveDayParts,
  });

  const {
    enableDelivery,
    ErrorDialog,
    handlePaymentMethodSelected,
    handleFirstpaySuccess,
    isCashPayment,
    isPlacingOrder,
    isValidTip,
    offerIsValid,
    paymentMethods,
    placeOrderAriaLabel,
    setCommitOrderAfterPricing,
    setIsHandlingOrderCommit,
    setShowAddPaymentMethod,
    shouldConfirmStoreOnCheckout,
    showAddPaymentMethod,
    showPaymentMethodSelection,
  } = useOrderPayment({
    enableOrderTimedFire,
    onPriceOrder,
    serverOrder,
  });

  const { getErrorMessages } = useGetPaymentErrors();

  const paymentErrorMessages = getErrorMessages({
    invalidOfferError: !offerIsValid,
    invalidPaymentMethodError: isPaymentError,
    invalidTipAmountError: !isValidTip,
    pricedAndAvailableError: !pricedAndAvailable,
  });

  const selectedMethod = paymentMethods.find(
    method => method.accountIdentifier === checkoutPaymentMethodId
  );

  const redirectCheckout = (): void => {
    window.location.href = '/cart';
  };

  const onPaymentOutcome = useCallback(
    (outcomefirstpay: OutcomeResult) => {
      setIsProcessingPaymentFirstpay(false);
      if (vaultedPaymentData) {
        setVaultedPaymentData(null);
      }

      if (outcomefirstpay.type === 'Failed') {
        logger.error({
          message: 'Firstpay Payment: Hosted page integration failed',
          outcome: outcomefirstpay,
        });

        setIsPaymentError(true);
        setIsCheckoutErrorsModalOpenFirstpay(true);
        if (showAddPaymentMethod) {
          setShowAddPaymentMethod(false);
          setTimeout(() => setShowAddPaymentMethod(true), 1000);
        }
        return;
      }
      const { merchantAccount, paymentMethod, pspReference } = outcomefirstpay;

      const payloadFirstpay = {
        merchantAccount,
        pspReference,
        isCardNumberValid: true,
        cardNumber: paymentMethod?.storePaymentMethod ? paymentMethod.storedPaymentMethodId : '',
        nameOnCard: paymentMethod?.cardholderName,
        isExpiryValid: true,
        card: {
          bin: paymentMethod?.bin,
          last4: paymentMethod?.last4,
          expiryMonth: paymentMethod?.expiryMonth,
          expiryYear: paymentMethod?.expiryYear,
        },
        cardType: paymentMethod?.brand,
        cvv: '',
        saveCard: paymentMethod?.storePaymentMethod,
        expiry: `${paymentMethod?.expiryMonth}${paymentMethod?.expiryYear}`,
        billingStreetAddress: null,
        billingCity: null,
        billingApt: null,
        billingZip: '',
        billingCountry: null,
        billingState: null,
        billingAddressSameAsDelivery: false,
      };

      handleFirstpaySuccess(payloadFirstpay);

      setIsHandlingOrderCommit(true);
    },
    [
      vaultedPaymentData,
      setIsHandlingOrderCommit,
      showAddPaymentMethod,
      setShowAddPaymentMethod,
      handleFirstpaySuccess,
    ]
  );

  if (!serverOrder || !serverOrder.rbiOrderId) {
    return null;
  }

  // Handles generating and auto-submitting a vaulted payment form if the user selects to pay with a saved credit card
  const handleVaultedFirstpayPayment = async () => {
    if (!selectedMethod || !selectedMethod.accountIdentifier) {
      return;
    }

    // If any error is present at last step we abort payment and show error modal
    if (paymentErrorMessages?.length) {
      setIsCheckoutErrorsModalOpenFirstpay(true);
      return;
    }

    setIsProcessingPaymentFirstpay(true);

    if (selectedMethod.accountIdentifier === CASH_ACCOUNT_IDENTIFIER) {
      shouldConfirmStoreOnCheckout(true);
      if (isCurbside && onPriceOrder) {
        setCommitOrderAfterPricing(true);
        return onPriceOrder({
          customerName: `CS-${LocalStorage.getItem(StorageKeys.VEHICLE_DESCRIPTION)}`,
        });
      }
      if (isTableService && onPriceOrder) {
        setCommitOrderAfterPricing(true);
        return onPriceOrder({
          customerName: `TABLE-${LocalStorage.getItem(StorageKeys.TABLE_NUMBER)}`,
        });
      }
      setIsHandlingOrderCommit(true);
      setIsProcessingPaymentFirstpay(false);
    }

    setIsHandlingOrderCommit(true);
  };

  const showPlaceOrderButtonFirstpay =
    (selectedMethod?.credit && !vaultedPaymentData) ||
    selectedMethod?.paypal ||
    selectedMethod?.cash;

  return (
    <StyledFullPageScreen heading={formatMessage({ id: 'orderPayment' })} closeHref={routes.cart}>
      {isCheckoutErrorsModalOpenFirstpay && (
        <CheckoutErrorsModal
          checkoutErrors={paymentErrorMessages}
          closeModal={() => {
            redirectCheckout();
            setIsCheckoutErrorsModalOpenFirstpay(false);
          }}
        />
      )}
      <PaymentSection data-testid="cart-hosted-page-payment">
        <SectionHeading id="payment-method-header">
          {formatMessage({ id: 'paymentDetails' })}
        </SectionHeading>
        <PaymentSelection>
          <Totals
            serverOrder={serverOrder}
            order={order}
            serviceMode={serviceMode}
            isDisabled={isProcessingPaymentFirstpay || isPlacingOrder}
          />
          {isDelivery && enableDelivery && <DeliveryCancellationPolicy />}
          <PaymentInfoBackground $hostedPayment>
            {showPaymentMethodSelection && (
              <PaymentMethod
                aria-expanded="true"
                aria-labelledby="payment-method-header"
                hasBorder={false}
                isAddCardOpen={showAddPaymentMethod}
                methods={paymentMethods}
                openAddCardOnClick={setShowAddPaymentMethod}
                handleSelect={handlePaymentMethodSelected}
                selectedMethodId={checkoutPaymentMethodId}
              />
            )}
            {showAddPaymentMethod && (
              <ConditionalWrapper
                condition={!paymentMethods.length}
                wrapper={children => <AddNewCreditCardWrapper>{children}</AddNewCreditCardWrapper>}
              >
                <FirstpayHostedPaymentForm rbiOrder={serverOrder} onResult={onPaymentOutcome} />
              </ConditionalWrapper>
            )}
            {isPlacingOrder && !isCashPayment && (
              <LoadingAnimation fillColor={Styles.color.primary} />
            )}
          </PaymentInfoBackground>
          {showPlaceOrderButtonFirstpay && (
            <PlaceOrderButton
              confirmation={false}
              disabled={isProcessingPaymentFirstpay || isPlacingOrder}
              isLoading={isProcessingPaymentFirstpay || isPlacingOrder}
              margin={selectedMethod ? '1rem 0 0' : '0.5rem 0 0'}
              onClick={handleVaultedFirstpayPayment}
              placeOrderAriaLabel={placeOrderAriaLabel}
            />
          )}
        </PaymentSelection>
        <ErrorDialog />
      </PaymentSection>
    </StyledFullPageScreen>
  );
};

export default FirstpayHostedPagePayment;
